/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, May 29th 2022, 2:25:09 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Stack from "@mui/material/Stack";
import { TextFormInput } from "ui-widgets/forms";
import { ReusableAuthForm } from "authentication/components/ReusableAuthForm";
import { useAPISSOSignUpForm, useAPIFetchSSOSignupInitialData } from "authentication/services/OAuthAPI";
import { SSOSignUpInput } from "lib/schemas";
import { useRedirectToSetup } from "authentication/hooks/useRedirectToSetup";
import { APIItemDisplayWrapper } from "ui-widgets/wrappers/APIRequestWrappers";
import { GoogleSignInError } from "./GoogleSignInError";

export const AdminSSOSignUpForm = () => {
  const { isFetchingSSOInitialData, isSSOInitialDataFetchError, useAPIFormReturn, ssoSignupInitialData } =
    useAdminSSOSignUpForm();

  return (
    <APIItemDisplayWrapper showLoader={isFetchingSSOInitialData}>
      {isSSOInitialDataFetchError ? (
        <GoogleSignInError />
      ) : (
        <ReusableAuthForm
          title={<>Create Account</>}
          description={
            <Stack spacing={2}>
              <p>
                Allow us to use your info from <b>Google</b> to create an account. Please also provide us with the name
                of the organization that should be created for your administration account.
              </p>
              <p>
                If your organization is already on Onestop, kindly request your organization administrator to invite you
                instead.
              </p>
            </Stack>
          }
          useAPIFormReturn={useAPIFormReturn}
        >
          <div className="form-options-seperator">
            <div className="fo-line"></div>
          </div>

          <div className="auth-title-wrapper">
            <p>
              <b>Email Address</b>
            </p>
            <p>{ssoSignupInitialData?.initial_data?.email}</p>
            <p>
              <b>Your Name</b>
            </p>
            <p>
              {ssoSignupInitialData?.initial_data?.first_name} {ssoSignupInitialData?.initial_data?.last_name}
            </p>
          </div>
          <TextFormInput<SSOSignUpInput> label="Organization Name" schema_field_key="organization_name" />
        </ReusableAuthForm>
      )}
    </APIItemDisplayWrapper>
  );
};

const useAdminSSOSignUpForm = () => {
  const { useAPIFormReturn, organization } = useAPISSOSignUpForm();

  const { ssoSignupInitialData, isFetchingSSOInitialData, isSSOInitialDataFetchError } =
    useAPIFetchSSOSignupInitialData();

  useRedirectToSetup(useAPIFormReturn.isSuccessfulAPIDataSend, organization);

  return { isFetchingSSOInitialData, isSSOInitialDataFetchError, useAPIFormReturn, ssoSignupInitialData };
};
